import type { MouseEventHandler } from 'react';

import ErrorBoundary from 'components/ErrorBoundary';
import { usePriceContext, useSelectedStore } from 'contexts';
import {
	useCart,
	useEngagementGoals,
	useFeatureToggle,
	useTrackEvent,
	useWishlist,
} from 'hooks';
import {
	type GTMItemListId,
	type GTMItemListName,
	useProductListGTMEvents,
} from 'hooks/gtm';
import type { BasketRowResponse } from 'models/api';
import type {
	ProductCard as ProductCardType,
	RelatedProductCard,
} from 'models/productCard';
import { getProductImage } from 'utils/business-logic';
import { getUnionProp } from 'utils/collection';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import ProductCardHorizontal, {
	type Props as ProductCardHorizontalProps,
} from './ProductCardHorizontal';
import ProductCardVertical, {
	type Props as ProductCardVerticalProps,
} from './ProductCardVertical';

interface Props {
	addToCartEngagementType?:
		| 'addAccessoryFromAdditionalSales'
		| 'addAccessoryToCart'
		| 'addArticleToCart';

	/** Extra container class names */
	className?: string;

	/** List ID for GTM events */
	gtmItemListId: GTMItemListId;

	/** List name for GTM events */
	gtmItemListName: GTMItemListName;

	/** If the column variant should have an even height with its neighbors. */
	isColumnEvenHeight?: boolean;

	/** Product added to cart callback */
	onAddToCart?: () => void;

	/** Product added to wishlist callback */
	onAddToWishlist?: () => void;

	/** Link click callback */
	onLinkClick?: MouseEventHandler<HTMLAnchorElement>;

	openAddedProductPopover?: boolean;

	/** Sets the orientation of the component */
	orientation?: 'horizontal' | 'vertical';

	/** Product data */
	product: ProductCardType | RelatedProductCard | BasketRowResponse;

	/** Zero based index in list */
	productListIndex: number;

	showAddToCartButton?: boolean;

	showAddToWishlistButton?: boolean;

	/** If any product tag belonging to the Price section should be displayed */
	showPriceSectionProductTag?: boolean;
}

export default function ProductCard({
	addToCartEngagementType = 'addArticleToCart',
	className,
	gtmItemListId,
	gtmItemListName,
	isColumnEvenHeight,
	onAddToCart,
	onAddToWishlist,
	onLinkClick,
	openAddedProductPopover = true,
	orientation = 'vertical',
	product,
	productListIndex,
	showAddToCartButton = false,
	showAddToWishlistButton = false,
	showPriceSectionProductTag = true,
}: Props) {
	const { t } = useI18n();
	const { addToCart } = useCart();
	const { addToWishlist } = useWishlist();
	const { usePriceExcVat } = usePriceContext();
	const { selectedStore } = useSelectedStore();
	const { sendProductClickEvent } = useTrackEvent();
	const { sendEngagement } = useEngagementGoals();
	const { onlineCommerceEnabled, storesEnabled, wishlistEnabled } =
		useFeatureToggle();
	const { sendAddToCartEvent, sendAddToWishlistEvent, sendSelectItemEvent } =
		useProductListGTMEvents(gtmItemListId, gtmItemListName);

	if (!product.url) {
		return null;
	}

	const variantId = 'variantId' in product ? product.variantId : product.id;

	const handleAddToCartClick = async () => {
		const cart = await addToCart(
			{
				variantId,
				metaData: {
					gtmItemListId,
					gtmItemListName,
				},
			},
			{
				returnAccessories: openAddedProductPopover,
				returnAddedProducts: true,
				showPopover: openAddedProductPopover,
				showToast: !openAddedProductPopover,
			},
		);
		sendAddToCartEvent(cart.id, product, 1);
		sendEngagement({ type: addToCartEngagementType });
		onAddToCart?.();
	};

	const handleAddToWishlistClick = async () => {
		await addToWishlist(
			{ variantId },
			{
				returnAccessories: openAddedProductPopover,
				returnAddedProducts: true,
				showPopover: openAddedProductPopover,
				showToast: !openAddedProductPopover,
			},
		);
		sendAddToWishlistEvent(product, 1);
		sendEngagement({ type: 'addArticleToWishlist' });
		onAddToWishlist?.();
	};

	const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
		sendSelectItemEvent(product, productListIndex);
		onLinkClick?.(e);
		sendProductClickEvent(variantId);
	};

	const legacyStockInfo =
		'productInStockAtStores' in product
			? {
					storeStock: {
						name: selectedStore?.name,
						inStock: Boolean(
							product.productInStockAtStores?.some(
								({ id }) => id.toString() === selectedStore?.id,
							),
						),
						isSellableInStore: product.isSellableInStore,
						productInStockAtStores: product.productInStockAtStoresCount ?? 0,
						isSellableOnWeb: product.isSellableOnWeb,
					},
					webStock: {
						isSellable: product.isSellable,
						isSellableOnWeb: product.isSellableOnWeb,
						productReStockWeb: product.productReStockWeb,
						isSellableInStore: product.isSellableInStore,
					},
				}
			: undefined;

	const isExpired = getUnionProp(product, 'expired') ?? false;
	const isSellable = product.isSellable ?? true;
	const price =
		getUnionProp(product, 'price') ?? getUnionProp(product, 'displayPrice');
	const reviewScore = getUnionProp(product, 'reviewScore');

	const brandTitle = product.brand?.title;
	const model = getUnionProp(product, 'model');
	const subheading =
		brandTitle && model ? `${brandTitle} | ${model}` : brandTitle;
	const srSubheading =
		brandTitle && model ? `${brandTitle}, ${model}` : undefined;

	const image = getProductImage(product);

	const commonProps: ProductCardHorizontalProps | ProductCardVerticalProps = {
		basePrice: product.basePrice,
		className,
		energySymbolSrc: getUnionProp(product, 'energySymbols')?.[0]?.src,
		flag: product.productTags?.find(({ section }) => section === 'Flag'),
		heading: product.title,
		href: product.url,
		imageSrc: image?.src,
		imageAlt: image?.alt,
		price: isExpired ? undefined : price,
		productSheetLink: getUnionProp(product, 'productSheetLink'),
		productSheetLinkDescription: getUnionProp(
			product,
			'productSheetLinkDescription',
		),
		reviews: reviewScore?.count,
		score: reviewScore?.score ?? 0,
		subheading,
		srSubheading,
		// Up to one of each Product/Price tag.
		tags: [
			product.productTags?.find(({ section }) => section === 'Product'),
			showPriceSectionProductTag &&
				product.productTags?.find(({ section }) => section === 'Price'),
		].filter(is.truthy),
		vatPriceText: usePriceExcVat ? t('price_vat_excluded_text') : undefined,
		onLinkClick: handleLinkClick,
		onAddToWishlistClick: handleAddToWishlistClick,
		onAddToCartClick: handleAddToCartClick,
		showAddToWishlistButton: !isExpired && showAddToWishlistButton,
		addToWishlistDisabled: !wishlistEnabled,
		showAddToCartButton: !isExpired && showAddToCartButton,
		addToCartDisabled: !isSellable || !onlineCommerceEnabled,
		stock: getUnionProp(product, 'stock'),
		storesEnabled,
		variantId,
		legacyStockInfo,
	};

	return (
		<ErrorBoundary>
			{orientation === 'horizontal' ? (
				<ProductCardHorizontal {...commonProps} />
			) : (
				<ProductCardVertical
					{...commonProps}
					checklist={getUnionProp(product, 'usPs')}
					multipleVariantIndicatorText={
						getUnionProp(product, 'multipleVariantNaming')?.indicatorText
					}
					isEvenHeight={isColumnEvenHeight}
					uspTags={product.productTags?.filter(
						({ section }) => section === 'Usp',
					)}
				/>
			)}
		</ErrorBoundary>
	);
}
ProductCard.displayName = 'ProductCard';
